import { MarketingLayout, Hero, BenefitCards, Steps, Faq, FinalCta, Note } from "../components/MarketingLayout";
import { useSeo } from "../lib/useSeo";

export default function Psychologists() {
  useSeo({
    title: "SimplyRefer for Psychologists | Join the GP Referral Directory",
    description:
      "Create a SimplyRefer profile so GP practices can find your clinical focus, availability and current referral status. Built for Australian psychologists.",
    canonicalPath: "/psychologists",
  });

  return (
    <MarketingLayout active="/psychologists">
      <Hero
        eyebrow="For Psychologists"
        h1="Help GP practices understand your clinical focus and referral availability."
        sub="SimplyRefer helps psychologists present their areas of expertise, referral preferences and current availability to local GP practices."
        primary={{ to: "/clinician/signup", label: "Join the directory" }}
        secondary={{ to: "/clinic/signup", label: "Register a clinic" }}
      />

      <BenefitCards items={[
        { title: "Be found by local GP practices", desc: "Maintain a clear professional profile visible to referring GPs." },
        { title: "Show your clinical focus", desc: "Present your areas of expertise, therapeutic focus, referral preferences and preferred client types." },
        { title: "Control your intake", desc: "Update whether you are open, waitlisted or closed so GP practices understand your current referral status." },
        { title: "Reduce unsuitable enquiries", desc: "Help reduce enquiries that do not match your availability, clinical focus or current capacity." },
        { title: "See meaningful GP engagement", desc: "Optional Insights show search appearances, profile views, referral selections and unique GP practices without patient information." },
        { title: "No patient information stored", desc: "You continue using your normal referral process. SimplyRefer does not store patient names or clinical information." },
      ]} />

      <section className="bg-white">
        <div className="mx-auto max-w-3xl px-5 pb-4 sm:px-8">
          <div className="rounded-xl border border-primary/30 bg-primary/[0.03] p-5 sm:p-6" data-testid="practitioner-pause-box">
            <p className="font-heading text-base font-semibold text-slate-900">Only pay when you're taking patients</p>
            <p className="mt-2 text-sm leading-relaxed text-slate-700">
              If you sign up as an individual practitioner and manage your own calendar, your subscription pauses when your books are closed, so you only pay when you are looking to take on new patients.
            </p>
            <ul className="mt-3 space-y-1.5 text-sm text-slate-600">
              <li>• Simple monthly subscription only — no annual commitment.</li>
              <li>• While your books are closed you stay on the directory for free, shown as not currently accepting referrals.</li>
              <li>• First 3 months free, no card required, beginning only when SimplyRefer launches.</li>
            </ul>
          </div>
        </div>
      </section>

      <Steps steps={[
        "Create your clinician profile.",
        "Add your areas of expertise and referral preferences.",
        "Keep your availability and referral status current.",
        "GP practices can find you when your profile matches their referral needs.",
        "See when a GP selects you through SimplyRefer, with optional references available when they need one.",
      ]} />

      <Note title="For clinics and practice managers">
        Clinic managers can manage multiple clinician profiles, availability and referral status from one account, helping keep directory information current across the practice.
      </Note>

      <Faq items={[
        { q: "Can I update whether I am accepting new referrals?", a: "Yes. SimplyRefer is designed so clinicians can keep their referral status current." },
        { q: "Can I show my clinical focus?", a: "Yes. Your profile can show your areas of practice, referral preferences and availability." },
        { q: "Does SimplyRefer store patient information?", a: "No. GPs continue using their normal referral letters. SimplyRefer records the referring practice and referral option, not patient information." },
      ]} />

      <FinalCta
        heading="Join the SimplyRefer directory."
        primary={{ to: "/clinician/signup", label: "Join the directory" }}
        secondary={{ to: "/clinic/signup", label: "Register a clinic" }}
      />
    </MarketingLayout>
  );
}
